import type { AppSocket } from "./socket";

const RESYNC_INTERVAL_MS = 30_000;
const SAMPLES_PER_ROUND = 3;
const SAMPLE_TIMEOUT_MS = 5_000;

let offsetMs = 0;

/** Best current estimate of "the server's Date.now()", per startClockSync()
 * below. Every timer countdown in this client compares a server-issued
 * absolute `endsAt` against this instead of raw Date.now() - the two only
 * agree if the client's system clock happens to match the server's exactly,
 * which players reported it sometimes doesn't (a timer reading several
 * seconds off from when the round actually ended). */
export function serverNow(): number {
  return Date.now() + offsetMs;
}

function takeSample(socket: AppSocket): Promise<{ offset: number; rtt: number } | null> {
  return new Promise((resolve) => {
    const t0 = Date.now();
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(null);
    }, SAMPLE_TIMEOUT_MS);
    socket.emit("time:sync", (serverTime) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      const t1 = Date.now();
      const rtt = t1 - t0;
      // Assumes the request and response legs took roughly equal time -
      // splitting the round trip down the middle is the standard NTP-style
      // estimate and is plenty accurate for a countdown display.
      resolve({ offset: serverTime + rtt / 2 - t1, rtt });
    });
  });
}

async function syncOnce(socket: AppSocket): Promise<void> {
  let best: { offset: number; rtt: number } | null = null;
  for (let i = 0; i < SAMPLES_PER_ROUND; i++) {
    const sample = await takeSample(socket);
    // Keep the lowest-RTT sample of the round - the fastest round trip is the
    // one least likely to have been distorted by a slow/congested moment on
    // the way there or back, so its offset estimate is the most trustworthy.
    if (sample && (!best || sample.rtt < best.rtt)) best = sample;
  }
  if (best) offsetMs = best.offset;
}

/** Estimates this client's clock offset from the server's immediately, then
 * keeps re-estimating periodically so gradual system-clock drift (or a
 * network path change) doesn't let the two quietly disagree again. */
export function startClockSync(socket: AppSocket): void {
  void syncOnce(socket);
  setInterval(() => void syncOnce(socket), RESYNC_INTERVAL_MS);
}
